import React, { useState, useEffect, useMemo, useRef } from "react";
import { CalendarCheck, Phone, Mail, User, Clock, MessageSquare } from "lucide-react";
import { useMessages } from "../context/MessagesContext";

// ---------------------------------------------------------------------------
// Política compartida con el backend (AppointmentScheduleValidator + ClockConfig).
// El backend es SIEMPRE la autoridad final de validación; estas constantes solo deciden qué
// opciones mostrar en el <select>. Se exportan para que un test de guardia
// (Reservar.policy.test.jsx) verifique que no haya divergencia con el backend.
// ---------------------------------------------------------------------------

// Misma zona horaria que ClockConfig (Clock.system(ZoneId.of("America/Tijuana"))).
export const TIJUANA_TZ = "America/Tijuana";

// Mismos horarios oficiales que AppointmentScheduleValidator.HOURS (clave = getDay() JS: 0=dom..6=sáb).
export const BUSINESS_HOURS = {
  0: { open: "09:00", close: "15:00" }, // domingo
  1: { open: "09:00", close: "17:00" }, // lunes
  2: { open: "09:00", close: "17:00" },
  3: { open: "09:00", close: "17:00" },
  4: { open: "09:00", close: "17:00" },
  5: { open: "09:00", close: "17:00" }, // viernes
  6: { open: "08:00", close: "15:00" }, // sábado
};

// Misma duración que google.calendar.event-duration-minutes (default backend = 30).
export const EVENT_DURATION_MINUTES = 30;

// Fecha y hora "ahora" en America/Tijuana, calculadas explícitamente con Intl.DateTimeFormat
// para no depender nunca de la zona horaria del navegador del paciente.
export function getTijuanaNow() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIJUANA_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const map = {};
  for (const p of parts) map[p.type] = p.value;

  return {
    date: `${map.year}-${map.month}-${map.day}`,
    time: `${map.hour}:${map.minute}`,
  };
}

function minutesSinceMidnight(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function formatHHMM(totalMinutes) {
  const h = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
  const m = String(totalMinutes % 60).padStart(2, "0");
  return `${h}:${m}`;
}

// Parsea "YYYY-MM-DD" como fecha de calendario (sin horas), evitando el corrimiento de día
// que puede producir `new Date(dateStr)` (lo interpreta en UTC) al leer getDay() localmente.
function dayOfWeekFromDateString(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1).getDay();
}

// Pura y determinista: recibe el "ahora" de Tijuana como argumento (no lo lee internamente),
// para que el componente pueda recalcular los slots cada vez que pasa el tiempo / cambia el día.
export function getAvailableSlots(dateStr, tijuanaNow) {
  if (!dateStr) return [];

  const hours = BUSINESS_HOURS[dayOfWeekFromDateString(dateStr)];
  if (!hours) return [];

  const openMinutes = minutesSinceMidnight(hours.open);
  const closeMinutes = minutesSinceMidnight(hours.close);

  const isToday = dateStr === tijuanaNow.date;
  const nowMinutes = isToday ? minutesSinceMidnight(tijuanaNow.time) : -1;

  const slots = [];
  for (let cur = openMinutes; cur + EVENT_DURATION_MINUTES <= closeMinutes; cur += 30) {
    // Para "hoy" solo se muestran horarios estrictamente futuros respecto a America/Tijuana;
    // el backend vuelve a validar esto de forma autoritativa de todas formas.
    if (!isToday || cur > nowMinutes) {
      slots.push(formatHHMM(cur));
    }
  }
  return slots;
}

export default function Reservar() {
  const labels = useMessages();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    preferredDate: "",
    preferredTime: "",
    comments: ""
  });

  const [requestId, setRequestId] = useState(() => crypto.randomUUID());

  // Snapshot de los datos enviados en el último intento (null = todavía no se ha intentado
  // enviar nada). Si el usuario reintenta con los MISMOS datos, se reutiliza requestId
  // (idempotencia). Si edita algo entre un intento fallido y el reintento, se genera un
  // requestId nuevo -- es, por definición, una solicitud distinta.
  const lastAttemptSnapshotRef = useRef(null);

  const [status, setStatus] = useState({
    type: "", // "success" | "error" | ""
    message: ""
  });

  const [fieldErrors, setFieldErrors] = useState({}); // { fullName: "...", email: "...", ... }
  const [loading, setLoading] = useState(false);

  // "Ahora" en America/Tijuana en estado, refrescado cada 60s: así, con la página abierta, los
  // horarios ya pasados desaparecen del selector y, al cruzar la medianoche de Tijuana, cambian
  // tanto el día mínimo del input de fecha como las opciones disponibles, sin recargar.
  const [tijuanaNow, setTijuanaNow] = useState(getTijuanaNow);
  useEffect(() => {
    const id = setInterval(() => setTijuanaNow(getTijuanaNow()), 60000);
    return () => clearInterval(id);
  }, []);

  // YYYY-MM-DD en America/Tijuana para min en date input (se permite el día de hoy)
  const tijuanaToday = tijuanaNow.date;

  const availableSlots = useMemo(
    () => getAvailableSlots(form.preferredDate, tijuanaNow),
    [form.preferredDate, tijuanaNow]
  );

  // Si cambia la fecha y la hora ya elegida deja de ser válida para el nuevo día, se limpia.
  useEffect(() => {
    if (form.preferredTime && !availableSlots.includes(form.preferredTime)) {
      setForm(prev => ({ ...prev, preferredTime: "" }));
    }
  }, [availableSlots, form.preferredTime]);

  // Auto-reset del mensaje a los 5 segundos
  useEffect(() => {
    if (!status.message) return;
    const t = setTimeout(() => setStatus({ type: "", message: "" }), 5000);
    return () => clearTimeout(t);
  }, [status]);

  function handleChange(e) {
    const { name, value } = e.target;

    // Limpia el error del campo mientras el usuario corrige
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    setForm(prev => ({ ...prev, [name]: value }));
  }

  function normalizeForm(f) {
    const clean = (s) => (s ?? "").toString().trim();

    // Normaliza espacios múltiples en nombre
    const normalizeSpaces = (s) => clean(s).replace(/\s+/g, " ");

    return {
      fullName: normalizeSpaces(f.fullName).slice(0, 80),
      email: clean(f.email).slice(0, 120),
      phone: clean(f.phone).slice(0, 30),
      preferredDate: clean(f.preferredDate).slice(0, 30),
      preferredTime: clean(f.preferredTime).slice(0, 30),
      comments: clean(f.comments).slice(0, 2000),
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});
    setStatus({ type: "", message: "" });

    const normalized = normalizeForm(form);
    const currentSnapshot = JSON.stringify(normalized);

    // Si ya hubo un intento previo y los datos cambiaron desde entonces, es una solicitud
    // deliberadamente distinta -> requestId nuevo. Si son exactamente los mismos datos
    // (reintento sin editar nada), se reutiliza el requestId actual.
    let effectiveRequestId = requestId;
    if (lastAttemptSnapshotRef.current !== null && lastAttemptSnapshotRef.current !== currentSnapshot) {
      effectiveRequestId = crypto.randomUUID();
      setRequestId(effectiveRequestId);
    }
    lastAttemptSnapshotRef.current = currentSnapshot;

    const payload = { ...normalized, requestId: effectiveRequestId };

    try {
      const res = await fetch("/api/contact/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        // Si el backend devuelve JSON con errores por campo, lo mostramos
        let data = null;
        const contentType = res.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          data = await res.json().catch(() => null);
        }

        if (res.status === 400 && data && typeof data === "object") {
          setFieldErrors(data);
          setStatus({
            type: "error",
            message: labels["reserve.error.validation"] || "❌ Revisa los campos marcados e inténtalo de nuevo."
          });
          return;
        }

        throw new Error(`Request failed (${res.status})`);
      }

      setStatus({
        type: "success",
        message: labels["reserve.success"] || "✅ Tu correo ha sido enviado. Pronto te contactaremos."
      });

      // Limpia form para que puedan enviar otro, y genera un nuevo requestId
      // (una solicitud deliberada nueva nunca debe compartir id con la anterior)
      setForm({
        fullName: "",
        email: "",
        phone: "",
        preferredDate: "",
        preferredTime: "",
        comments: ""
      });
      setRequestId(crypto.randomUUID());
      lastAttemptSnapshotRef.current = null;
    } catch (err) {
      setStatus({
        type: "error",
        message: labels["reserve.error"] || "❌ No se pudo enviar el correo. Intenta de nuevo."
      });
      // Nota: requestId NO se regenera en un fallo, para que un reintento del mismo intento
      // reutilice el mismo identificador (idempotencia del lado de Calendar en el backend).
    } finally {
      setLoading(false);
    }
  }

  // Validación ligera UX para deshabilitar botón si falta algo
  const isFormValid =
    form.fullName.trim().length > 0 &&
    form.email.trim().length > 0 &&
    form.phone.trim().length > 0 &&
    form.preferredDate.trim().length > 0 &&
    form.preferredTime.trim().length > 0;

  const inputErrorClass = "border-red-300 focus:ring-red-200";
  const helpErrorClass = "text-xs text-red-600 mt-1";

  return (
    <section id="reservar" className="section-elegant scroll-mt-24">
      <div className="container mx-auto px-4 py-6 lg:px-8">

        {/* Header estilo "Quiénes Somos" */}
        <div className="text-center mb-12 pt-8">
          <h2 className="section-title">
            {labels["reserve.title"] || "Agenda tu Cita"}
            </h2>
          <p className="body-copy mt-4 max-w-2xl mx-auto">
            {labels["reserve.subtitle"] ||
              "Déjanos tus datos y el horario que prefieres. Nuestro equipo te confirmará tu cita lo antes posible."}
          </p>
        </div>

        {/* Card contenedora */}
        <div className="section-surface max-w-3xl mx-auto p-6 md:p-10">

          {/* Mensaje de estado */}
          {status.message && (
            <div
              className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium ${status.type === "success"
                ? "bg-skyblue text-turquoise border border-turquoise/30"
                : "bg-red-50 text-red-700 border border-red-200"
                }`}
            >
              {status.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Nombre */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.fullName"] || "Nombre completo"}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                  maxLength={80}
                  autoComplete="name"
                  placeholder="Ej. Juan Pérez"
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-turquoise/40 ${fieldErrors.fullName ? inputErrorClass : ""}`}
                />
              </div>
              {fieldErrors.fullName && <p className={helpErrorClass}>{fieldErrors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.email"] || "Correo electrónico"}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  maxLength={120}
                  autoComplete="email"
                  placeholder="email@msn.com"
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-turquoise/40 ${fieldErrors.email ? inputErrorClass : ""}`}
                />
              </div>
              {fieldErrors.email && <p className={helpErrorClass}>{fieldErrors.email}</p>}
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.phone"] || "Teléfono"}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  maxLength={30}
                  autoComplete="tel"
                  inputMode="tel"
                  pattern="^[0-9+\-\s()]{7,30}$"
                  title="Usa solo números, espacios, +, -, paréntesis (7 a 30 caracteres)"
                  placeholder="+52 55 1234 5678"
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-turquoise/40 ${fieldErrors.phone ? inputErrorClass : ""}`}
                />
              </div>
              {fieldErrors.phone && <p className={helpErrorClass}>{fieldErrors.phone}</p>}
            </div>

            {/* Fecha */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.date"] || "Fecha preferida"}
              </label>
              <div className="relative">
                <CalendarCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  name="preferredDate"
                  value={form.preferredDate}
                  onChange={handleChange}
                  required
                  min={tijuanaToday}
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-turquoise/40 ${fieldErrors.preferredDate ? inputErrorClass : ""}`}
                />
              </div>
              {fieldErrors.preferredDate && <p className={helpErrorClass}>{fieldErrors.preferredDate}</p>}
            </div>

            {/* Horario */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.time"] || "Horario preferido"}
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  name="preferredTime"
                  value={form.preferredTime}
                  onChange={handleChange}
                  required
                  disabled={!form.preferredDate}
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-turquoise/40 disabled:bg-gray-50 disabled:text-gray-400 ${fieldErrors.preferredTime ? inputErrorClass : ""}`}
                >
                  <option value="">{labels["reserve.selectTime"] || "Selecciona un horario"}</option>
                  {availableSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>
              {fieldErrors.preferredTime && <p className={helpErrorClass}>{fieldErrors.preferredTime}</p>}
            </div>

            {/* Comentarios */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {labels["reserve.comments"] || "Comentarios adicionales"}
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <textarea
                  name="comments"
                  value={form.comments}
                  onChange={handleChange}
                  rows={4}
                  maxLength={2000}
                  placeholder={labels["reserve.comments.placeholder"] || "Cuéntanos qué tratamiento te interesa o cualquier detalle importante..."}
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-turquoise/40 resize-none ${fieldErrors.comments ? inputErrorClass : ""}`}
                />
              </div>
              <div className="flex items-center justify-between">
                {fieldErrors.comments ? (
                  <p className={helpErrorClass}>{fieldErrors.comments}</p>
                ) : (
                  <span className="text-xs text-gray-400 mt-1">
                    {form.comments.length}/2000
                  </span>
                )}
              </div>
            </div>

            {/* Botón enviar */}
            <div className="md:col-span-2 flex justify-center mt-2">
              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="btn btn--primary btn--shadow px-8 py-3 text-base flex items-center gap-2 disabled:opacity-60"
              >
                <Mail className="w-5 h-5" />
                {loading
                  ? (labels["reserve.sending"] || "Enviando...")
                  : (labels["reserve.send"] || "Enviar solicitud")}
              </button>
            </div>

          </form>
        </div>
      </div>
    </section>
  );
}
